import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Flame, Snowflake, Users, CurrencyEur, CalendarCheck, Star, CheckCircle, X } from "@phosphor-icons/react";

export default function Admin() {
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [filter, setFilter] = useState("all");
  const [reviewFilter, setReviewFilter] = useState("pending");

  const loadReviews = () =>
    api.get("/admin/reviews").then(({ data }) => setReviews(data)).catch(() => setReviews([]));

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setStats({
      today_bookings: 0, total_bookings: 0, revenue_eur: 0, users: 0, today_occupancy: [],
    }));
    api.get("/admin/bookings").then(({ data }) => setBookings(data)).catch(() => setBookings([]));
    loadReviews();
  }, []);

  const moderate = async (review_id, status) => {
    try { await api.patch(`/admin/reviews/${review_id}`, { status }); loadReviews(); } catch { /* ignore */ }
  };

  const filtered = (bookings || []).filter((b) => filter === "all" ? true : b.status === filter);
  const filteredReviews = (reviews || []).filter((r) => reviewFilter === "all" ? true : r.status === reviewFilter);
  const pendingCount = (reviews || []).filter((r) => r.status === "pending").length;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="admin-page">
      <header className="mb-12 fade-in">
        <p className="overline">— Painel</p>
        <h1 className="font-display text-5xl md:text-6xl mt-3 text-[#003D4C]">Operações</h1>
      </header>

      <div className="grid md:grid-cols-4 gap-4 mb-12">
        <Kpi icon={<CalendarCheck size={20} weight="light" color="#BE3A34" />} label="Reservas hoje"
             value={stats?.today_bookings ?? "—"} testid="kpi-today" />
        <Kpi icon={<CalendarCheck size={20} weight="light" />} label="Reservas totais"
             value={stats?.total_bookings ?? "—"} testid="kpi-total" />
        <Kpi icon={<CurrencyEur size={20} weight="light" color="#6BB9B8" />} label="Receita"
             value={stats ? `€${stats.revenue_eur.toFixed(2)}` : "—"} testid="kpi-revenue" />
        <Kpi icon={<Users size={20} weight="light" />} label="Clientes"
             value={stats?.users ?? "—"} testid="kpi-users" />
      </div>

      <div className="flex gap-6 border-b hairline mb-8" data-testid="admin-tabs">
        <TabBtn id="bookings" active={tab} setTab={setTab}>Reservas</TabBtn>
        <TabBtn id="occupancy" active={tab} setTab={setTab}>Ocupação de hoje</TabBtn>
        <TabBtn id="reviews" active={tab} setTab={setTab} badge={pendingCount}>Reviews</TabBtn>
      </div>

      {tab === "occupancy" && stats && (
        <section data-testid="occupancy-section">
          <p className="text-xs text-[#003D4C]/55 mb-4">Capacidade 8 / hora · 8h–20h</p>
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-2" data-testid="occupancy-grid">
            {stats.today_occupancy.map((s) => (
              <div key={s.hour} className="border hairline bg-white p-3" data-testid={`occ-${s.hour}`}>
                <div className="font-display text-xl text-[#003D4C]">{String(s.hour).padStart(2, "0")}h</div>
                <div className="mt-2 flex items-center gap-1 text-xs text-[#003D4C]/65">
                  <Flame size={12} weight="light" color="#BE3A34" /> {s.sauna_used}/{s.capacity}
                </div>
                <div className="mt-1 flex items-center gap-1 text-xs text-[#003D4C]/65">
                  <Snowflake size={12} weight="light" color="#6BB9B8" /> {s.ice_used}/{s.capacity}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "bookings" && (
        <section>
          <div className="flex items-center justify-end mb-4">
            <div className="flex gap-2 flex-wrap">
              {["all", "confirmed", "pending", "expired"].map((f) => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                    filter === f ? "border-[#BE3A34] text-[#BE3A34]" : "hairline text-[#003D4C]/65 hover:text-[#003D4C] hover:border-[#003D4C]/35"
                  }`}
                  data-testid={`filter-${f}`}>{f === "all" ? "Todas" : f}</button>
              ))}
            </div>
          </div>
          {!bookings ? (
            <p className="text-[#003D4C]/55 text-sm">A carregar…</p>
          ) : filtered.length === 0 ? (
            <p className="text-[#003D4C]/55 text-sm" data-testid="admin-empty">Sem reservas para este filtro.</p>
          ) : (
            <div className="border hairline bg-white overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-bookings-table">
                <thead className="text-left text-[#003D4C]/55 text-xs uppercase tracking-wider">
                  <tr className="border-b hairline">
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Serviço</th>
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3">Hora</th>
                    <th className="px-4 py-3">€</th>
                    <th className="px-4 py-3">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr key={b.booking_id} className="border-b hairline-soft last:border-0" data-testid={`row-${b.booking_id}`}>
                      <td className="px-4 py-3">
                        <div className="text-[#003D4C]">{b.user_name || "—"}</div>
                        <div className="text-xs text-[#003D4C]/55">{b.user_email}</div>
                      </td>
                      <td className="px-4 py-3 text-[#003D4C]">{b.service_label}</td>
                      <td className="px-4 py-3 text-[#003D4C]">{b.date}</td>
                      <td className="px-4 py-3 text-[#003D4C]">{String(b.hour).padStart(2, "0")}:00</td>
                      <td className="px-4 py-3 font-display text-base text-[#003D4C]">€{b.amount}</td>
                      <td className="px-4 py-3 uppercase text-xs tracking-wider">
                        <span className={
                          b.status === "confirmed" ? "text-[#6BB9B8]" :
                          b.status === "pending" ? "text-[#D69A2D]" : "text-[#003D4C]/50"
                        }>{b.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === "reviews" && (
        <section data-testid="reviews-admin-section">
          <div className="flex justify-end mb-4">
            <div className="flex gap-2 flex-wrap">
              {["pending", "approved", "rejected", "all"].map((f) => (
                <button key={f} onClick={() => setReviewFilter(f)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                    reviewFilter === f ? "border-[#BE3A34] text-[#BE3A34]" : "hairline text-[#003D4C]/65 hover:text-[#003D4C] hover:border-[#003D4C]/35"
                  }`}
                  data-testid={`review-filter-${f}`}>{f === "all" ? "Todas" : f}</button>
              ))}
            </div>
          </div>
          {!reviews ? (
            <p className="text-[#003D4C]/55 text-sm">A carregar…</p>
          ) : filteredReviews.length === 0 ? (
            <p className="text-[#003D4C]/55 text-sm" data-testid="reviews-admin-empty">Sem reviews para este filtro.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {filteredReviews.map((r) => (
                <article key={r.review_id} className="card" data-testid={`admin-review-${r.review_id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="text-sm text-[#003D4C]">{r.user_name}</div>
                      <div className="text-xs text-[#003D4C]/55 mt-0.5">{r.service_label} · {new Date(r.created_at).toLocaleDateString("pt-PT")}</div>
                    </div>
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} size={14} weight={i < r.rating ? "fill" : "regular"} color={i < r.rating ? "#D69A2D" : "#9D968D"} />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-[#003D4C]/85 leading-relaxed">&ldquo;{r.comment}&rdquo;</p>
                  <div className="mt-5 pt-4 border-t hairline-soft flex items-center justify-between gap-3">
                    <span className={`text-xs uppercase tracking-wider ${
                      r.status === "approved" ? "text-[#6BB9B8]" :
                      r.status === "rejected" ? "text-[#003D4C]/45" : "text-[#D69A2D]"
                    }`}>{r.status}</span>
                    {r.status === "pending" && (
                      <div className="flex gap-2">
                        <button onClick={() => moderate(r.review_id, "rejected")} className="btn-ghost px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`reject-${r.review_id}`}>
                          <X size={12} weight="light" /> Rejeitar
                        </button>
                        <button onClick={() => moderate(r.review_id, "approved")} className="btn-hot px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`approve-${r.review_id}`}>
                          <CheckCircle size={12} weight="fill" /> Aprovar
                        </button>
                      </div>
                    )}
                    {r.status !== "pending" && (
                      <button onClick={() => moderate(r.review_id, r.status === "approved" ? "rejected" : "approved")} className="btn-ghost px-3 py-1.5 text-xs" data-testid={`toggle-${r.review_id}`}>
                        {r.status === "approved" ? "Despublicar" : "Aprovar"}
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, testid }) {
  return (
    <div className="kpi" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#003D4C]/65 text-xs uppercase tracking-wider">{icon} {label}</div>
      <div className="font-display text-4xl mt-3 text-[#003D4C]">{value}</div>
    </div>
  );
}

function TabBtn({ id, active, setTab, children, badge }) {
  const isActive = active === id;
  return (
    <button onClick={() => setTab(id)} data-testid={`tab-${id}`}
      className={`pb-3 -mb-px text-sm tracking-wide flex items-center gap-2 border-b-2 transition-colors ${
        isActive ? "text-[#003D4C] border-[#BE3A34]" : "text-[#003D4C]/55 border-transparent hover:text-[#003D4C]"
      }`}>
      {children}
      {badge > 0 && (
        <span className="bg-[#BE3A34] text-[#F5F1EA] text-[10px] px-1.5 py-0.5 leading-none">{badge}</span>
      )}
    </button>
  );
}
